import Box from "@mui/material/Box";
import {
	langCountryCodesDictionary,
	langDashCodesDictionary,
	USER_ROLES,
	USER_ROLES_ARRAY,
} from "../../../../constants";
import EditButton from "./EditButton";
import { LoginAsButton } from "./LoginAsButton";
import RemoveButton from "./RemoveButton";

function timeSinceLastLogin(lastLoginTimestamp, t, lang) {
	const now = new Date();
	const lastLogin = new Date(lastLoginTimestamp);

	// convert both dates to UTC
	const utcNow = Date.UTC(
		now.getUTCFullYear(),
		now.getUTCMonth(),
		now.getUTCDate(),
		now.getUTCHours(),
		now.getUTCMinutes(),
		now.getUTCSeconds()
	);
	const utcLastLogin = Date.UTC(
		lastLogin.getUTCFullYear(),
		lastLogin.getUTCMonth(),
		lastLogin.getUTCDate(),
		lastLogin.getUTCHours(),
		lastLogin.getUTCMinutes(),
		lastLogin.getUTCSeconds()
	);

	const secondsElapsed = Math.floor((utcNow - utcLastLogin) / 1000);
	const rtf = new Intl.RelativeTimeFormat(
		langCountryCodesDictionary[lang] || "pl",
		{
			numeric: "auto",
		}
	);

	const units = [
		{ unit: "year", seconds: 60 * 60 * 24 * 365 },
		{ unit: "month", seconds: 60 * 60 * 24 * 30 },
		{ unit: "week", seconds: 60 * 60 * 24 * 7 },
		{ unit: "day", seconds: 60 * 60 * 24 },
		{ unit: "hour", seconds: 60 * 60 },
		{ unit: "minute", seconds: 60 },
		{ unit: "second", seconds: 1 },
	];

	for (const { unit, seconds } of units) {
		const value = Math.floor(secondsElapsed / seconds);
		if (value !== 0) {
			return rtf.format(-value, unit);
		}
	}

	return t("now");
}

export const getColumns = (t, lang) => {
	return [
		{
			label: t("company_id"),
			name: "companyId",
			options: {
				display: false,
			},
		},
		{
			label: t("first_name_label"),
			name: "first_name",
		},
		{
			label: t("surname_label"),
			name: "last_name",
		},
		{ label: t("email_label"), name: "email" },
		{ label: t("phone_label"), name: "phone_number" },
		{ label: t("branch"), name: "branch" },
		{
			label: t("role"),
			name: "role",
			options: {
				customBodyRender: (value) => {
					const roles = USER_ROLES_ARRAY;

					return (
						<div>{roles.includes(value) ? t(USER_ROLES[value]) : "-"}</div>
					);
				},
			},
		},
		{
			label: t("last_activity"),
			name: "last_login",
			options: {
				customBodyRender: (value) => {
					if (!value) {
						return "-";
					}
					const lastSeen = new Date(value);

					return timeSinceLastLogin(lastSeen, t, lang);
				},
			},
		},
		{
			label: t("added_date"),
			name: "created_at",
			options: {
				customBodyRender: (value) => {
					const date = new Date(value).toLocaleDateString(
						langDashCodesDictionary[lang] || "pl-PL",
						{
							day: "numeric",
							month: "numeric",
							year: "numeric",
						}
					);

					return <div>{date}</div>;
				},
			},
		},
		{
			label: t("actions"),
			name: "id",
			options: {
				filter: false,
				sort: false,
				empty: true,
				customBodyRender: (value, tableMeta, updateValue) => {
					const companyId = tableMeta.rowData[0];
					return (
						<Box sx={{ display: "flex", gap: "6px" }}>
							<RemoveButton userId={value} />
							<EditButton userId={value} companyId={companyId} />
							<LoginAsButton userId={value} />
						</Box>
					);
				},
			},
		},
	];
};

export const getOptions = (t) => {
	return {
		searchOpen: true,
		search: false,
		searchAlwaysOpen: true,
		searchPlaceholder: t("search"),
		selectableRows: "none",
		print: false,
		filter: false,
		download: true,
		viewColumns: false,
		rowsPerPageOptions: [10, 20, 50, 100],
		rowsPerPage: 20,
		textLabels: {
			pagination: {
				next: t("next_page"),
				previous: t("previous_page"),
				rowsPerPage: t("rows_per_page"),
				displayRows: t("of"),
			},
			toolbar: {
				search: t("search"),
				downloadCsv: t("download_csv"),
				print: t("print"),
				viewColumns: t("select_columns"),
				filterTable: t("filters"),
			},
			filter: {
				title: t("filters"),
				reset: t("reset"),
				all: t("all"),
			},
			viewColumns: {
				title: t("show_columns"),
			},
			selectedRows: {
				text: t("removed_rows"),
				delete: t("remove"),
			},
			body: {
				noMatch: t("no_matching_data_found"),
			},
		},
	};
};
